import type { SearchInterpretation } from '../../api/hospitals'

/**
 * Where a search ends (M33-T03, ADR-0062).
 *
 * In its own module because the PANEL and the PAGE AROUND IT both have to
 * branch on it. The specialty chips and the "Request a specialist" door are
 * answers too, and while each decided for itself the screen offered four of
 * them at once on `shortness of breath`. One function, two callers, no drift.
 */
/** Who can take an unclear presentation and refer it on (ADR-0062,
 * destination 2). Ordered by preference, so the set is small and explicit
 * rather than a rule about specialty kinds. */
export const GENERALIST_SLUGS = ['general-practice', 'internal-medicine'] as const

/** The one of ADR-0062's three destinations this search reached. */
export type SearchDestination =
  | 'emergency'
  | 'specialists'
  | 'generalist'
  | 'concierge'

export function searchDestination(
  interpretation: SearchInterpretation,
): SearchDestination {
  // THE SERVER DECIDES (M33-T04). It has to: the result list is scoped by the
  // same rule, and a second copy of a product rule is how one screen came to
  // show four answers at once. The local derivation below is the fallback for a
  // server older than M33-T04, and must not drift from `destination.py`.
  if (interpretation.destination !== undefined) return interpretation.destination

  if (interpretation.route === 'emergency') return 'emergency'

  // A procedure somebody offers IS an answer, whatever the specialties say.
  // `x-ray` resolves Radiology (nobody staffs it) while five hospitals offer the
  // scan, and reading `best` first called that a dead end.
  if ((interpretation.procedures ?? []).some((p) => p.offer_count > 0))
    return 'specialists'

  const best = interpretation.understood_as[0] ?? null
  if (best === null) return 'concierge'
  if (best.offered) return 'specialists'
  const hasGeneralist = interpretation.understood_as.some(
    (s) => s.offered && (GENERALIST_SLUGS as readonly string[]).includes(s.slug),
  )
  return hasGeneralist ? 'generalist' : 'concierge'
}

/** The specialty this answer is about, to carry into the result and the profile
 * (M33-T04). The best fit when somebody staffs it, the generalist when nobody
 * does, and nothing at all when there is no answer to carry. */
export function answeringSpecialty(
  interpretation: SearchInterpretation,
): string | null {
  switch (searchDestination(interpretation)) {
    case 'specialists': {
      // ONE SPECIALTY AT A TIME (M33-T04c, owner 2026-10-14). The list holds
      // the best fit alone, so every row is there for it and it is always the
      // right thing to carry. Only when it is staffed: an offered PROCEDURE
      // also lands here (`x-ray`, Radiology unstaffed), and an unstaffed
      // specialty has no tab to open.
      const best = interpretation.understood_as[0]
      return best !== undefined && best.offered ? best.slug : null
    }
    case 'generalist': {
      // ONE generalist, or none. With both staffed the list is decided per
      // hospital (M33-T06b: GP first, an internist only where a hospital has no
      // GP), so neither slug is right for every row.
      const staffed = new Set(
        interpretation.understood_as.filter((s) => s.offered).map((s) => s.slug),
      )
      const found = GENERALIST_SLUGS.filter((slug) => staffed.has(slug))
      return found.length === 1 ? (found[0] ?? null) : null
    }
    default:
      return null
  }
}
